import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
} from "@mui/material";
import {
  Assignment,
  Build,
  CheckCircle,
  Error,
  Send,
  PhotoCamera,
  CloudUpload,
  Delete,
  Schedule,
  Comment,
  ExpandMore,
  Refresh,
  ArrowBack,
  Download,
  Visibility,
  Close,
  NavigateNext,
  NavigateBefore,
  Edit,
} from "@mui/icons-material";

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";

import { useParams, useNavigate } from "react-router-dom";
import PpMobilePhotoCaptureComponent from "../Photos/PpMobilePhotoCaptureComponent";
import PpPhotoAnnotationTools from "../Photos/PpPhotoAnnotationTools";

const ContractorPermitInspectionsPage = () => {
  const { id: permitId } = useParams();
  const navigate = useNavigate();

  const [permit, setPermit] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [inspectionDialog, setInspectionDialog] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [resubmitFiles, setResubmitFiles] = useState([]);
  const [resubmitComments, setResubmitComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentInspection, setCommentInspection] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  // Photo capture and annotation states
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Mock permit and inspection data
  const mockPermit = useMemo(
    () => ({
      id: permitId,
      number: `PKG${permitId}`,
      address: "123 Oak Street, Jacksonville, FL 32207",
      type: "residential",
      status: "active",
      contractor: {
        name: "ABC Construction",
        license: "CBC123456",
        phone: "(904) 555-0123",
      },
      submittedDate: "2024-01-15",
      approvedDate: "2024-01-30",
    }),
    [permitId]
  );

  const mockInspections = useMemo(
    () => [
      {
        id: "I001",
        name: "Footing/Foundation",
        type: "structural",
        required: true,
        status: "passed",
        scheduledDate: "2024-02-01",
        completedDate: "2024-02-01",
        inspector: "John Smith",
        dependencies: [],
        submittedFiles: [
          {
            id: "F1",
            name: "foundation_photos_1.jpg",
            filename: "foundation_photos_1.jpg",
            uploadDate: "2024-01-31",
            url: "https://via.placeholder.com/800x600/0066cc/ffffff?text=Foundation+Photo+1",
            dataUrl:
              "https://via.placeholder.com/800x600/0066cc/ffffff?text=Foundation+Photo+1",
          },
          {
            id: "F2",
            name: "foundation_photos_2.jpg",
            filename: "foundation_photos_2.jpg",
            uploadDate: "2024-01-31",
            url: "https://via.placeholder.com/800x600/0066cc/ffffff?text=Foundation+Photo+2",
            dataUrl:
              "https://via.placeholder.com/800x600/0066cc/ffffff?text=Foundation+Photo+2",
          },
        ],
        comments: [
          {
            id: "C1",
            author: "Contractor",
            date: "2024-01-31",
            text: "Foundation work completed as per plans.",
          },
          {
            id: "C2",
            author: "Inspector (John Smith)",
            date: "2024-02-01",
            text: "Foundation inspected and approved. Good work.",
          },
        ],
        history: [
          { date: "2024-01-31", action: "Requested", user: "Contractor" },
          { date: "2024-02-01", action: "Approved", user: "John Smith" },
          { date: "2024-02-01", action: "Passed", user: "John Smith" },
        ],
      },
      {
        id: "I002",
        name: "Framing",
        type: "structural",
        required: true,
        status: "failed",
        scheduledDate: "2024-02-15",
        completedDate: "2024-02-15",
        inspector: "John Smith",
        dependencies: ["I001"],
        failureReasons: [
          "Beam size does not match plans on 2nd floor",
          "Missing joist hangers at main beam connection",
        ],
        correctionRequired: "Address structural framing issues and resubmit",
        codeReferences: ["IRC R502.6", "IRC R502.6.1"],
        submittedFiles: [
          {
            id: "F3",
            name: "framing_photos_1.jpg",
            filename: "framing_photos_1.jpg",
            uploadDate: "2024-02-14",
            url: "https://via.placeholder.com/800x600/ff6600/ffffff?text=Framing+Photo+1",
            dataUrl:
              "https://via.placeholder.com/800x600/ff6600/ffffff?text=Framing+Photo+1",
          },
          {
            id: "F4",
            name: "framing_photos_2.jpg",
            filename: "framing_photos_2.jpg",
            uploadDate: "2024-02-14",
            url: "https://via.placeholder.com/800x600/ff6600/ffffff?text=Framing+Photo+2",
            dataUrl:
              "https://via.placeholder.com/800x600/ff6600/ffffff?text=Framing+Photo+2",
          },
        ],
        comments: [
          {
            id: "C3",
            author: "Contractor",
            date: "2024-02-14",
            text: "Framing inspection ready.",
          },
          {
            id: "C4",
            author: "Inspector (John Smith)",
            date: "2024-02-15",
            text: "Multiple framing issues found. See failure report for details.",
          },
        ],
        history: [
          { date: "2024-02-14", action: "Requested", user: "Contractor" },
          { date: "2024-02-15", action: "Approved", user: "John Smith" },
          { date: "2024-02-15", action: "Failed", user: "John Smith" },
        ],
      },
      {
        id: "I003",
        name: "Electrical Rough-In",
        type: "electrical",
        required: true,
        status: "pending",
        scheduledDate: null,
        completedDate: null,
        inspector: null,
        dependencies: ["I002"],
        submittedFiles: [],
        comments: [],
        history: [
          { date: "2024-02-16", action: "Requested", user: "Contractor" },
        ],
      },
      {
        id: "I004",
        name: "Insulation",
        type: "energy",
        required: false,
        status: "not_requested",
        dependencies: ["I003"],
        submittedFiles: [],
        comments: [],
        history: [],
      },
    ],
    []
  );

  useEffect(() => {
    // Simulate loading permit and inspection data
    setPermit(mockPermit);
    setInspections(mockInspections);
  }, [permitId, mockPermit, mockInspections]);

  const getStatusChip = (status) => {
    const statusConfig = {
      not_requested: { color: "default", label: "Not Requested" },
      pending: { color: "warning", label: "Pending" },
      approved: { color: "primary", label: "Approved" },
      passed: { color: "success", label: "Passed" },
      failed: { color: "error", label: "Failed" },
      corrections_needed: { color: "warning", label: "Needs Correction" },
      partially_approved: { color: "info", label: "Partially Approved" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "passed":
        return <CheckCircle color="success" />;
      case "failed":
        return <Error color="error" />;
      case "pending":
        return <Schedule color="warning" />;
      case "approved":
        return <Assignment color="primary" />;
      default:
        return <Build color="disabled" />;
    }
  };

  const canResubmit = (inspection) => {
    return (
      inspection.status === "failed" ||
      inspection.status === "corrections_needed"
    );
  };

  const handleResubmitInspection = async () => {
    if (!selectedInspection || resubmitFiles.length === 0) return;

    try {
      setSubmitting(true);

      // Mock API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Update inspection status
      setInspections((prev) =>
        prev.map((insp) =>
          insp.id === selectedInspection.id
            ? {
                ...insp,
                status: "pending",
                submittedFiles: [
                  ...insp.submittedFiles,
                  ...resubmitFiles.map((file, index) => ({
                    id: `F_NEW_${Date.now()}_${index}`,
                    name: file.name,
                    uploadDate: new Date().toISOString().split("T")[0],
                  })),
                ],
                comments: [
                  ...insp.comments,
                  {
                    id: `C_NEW_${Date.now()}`,
                    author: "Contractor",
                    date: new Date().toISOString().split("T")[0],
                    text:
                      resubmitComments ||
                      "Corrections uploaded for re-inspection.",
                  },
                ],
                history: [
                  ...insp.history,
                  {
                    date: new Date().toISOString().split("T")[0],
                    action: "Resubmitted",
                    user: "Contractor",
                  },
                ],
              }
            : insp
        )
      );

      setInspectionDialog(false);
      setSelectedInspection(null);
      setResubmitFiles([]);
      setResubmitComments("");
    } catch (error) {
      console.error("Failed to resubmit:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file, index) => ({
      id: `TEMP_${Date.now()}_${index}`,
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
      file,
    }));

    setResubmitFiles([...resubmitFiles, ...newFiles]);
  };

  const handleRemoveFile = (fileId) => {
    setResubmitFiles(resubmitFiles.filter((f) => f.id !== fileId));
  };

  const handleViewFile = (file, allFiles) => {
    setSelectedFiles(allFiles);
    setCurrentFileIndex(allFiles.findIndex((f) => f.id === file.id));
    setSelectedFile(file);
    setFileViewerOpen(true);
  };

  const handleNextFile = () => {
    if (currentFileIndex < selectedFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
      setSelectedFile(selectedFiles[currentFileIndex + 1]);
    }
  };

  const handlePrevFile = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
      setSelectedFile(selectedFiles[currentFileIndex - 1]);
    }
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedFile(null);
    setSelectedFiles([]);
    setCurrentFileIndex(0);
  };

  const handleAddComment = (inspection) => {
    setCommentInspection(inspection);
    setNewComment("");
    setCommentDialogOpen(true);
  };

  const handleSubmitComment = async () => {
    if (!commentInspection || !newComment.trim()) return;

    try {
      setSubmittingComment(true);

      // Mock API call to submit comment
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Update inspection with new comment
      setInspections((prev) =>
        prev.map((insp) =>
          insp.id === commentInspection.id
            ? {
                ...insp,
                comments: [
                  ...insp.comments,
                  {
                    id: `C_NEW_${Date.now()}`,
                    author: "Contractor",
                    date: new Date().toISOString().split("T")[0],
                    text: newComment.trim(),
                  },
                ],
              }
            : insp
        )
      );

      setCommentDialogOpen(false);
      setCommentInspection(null);
      setNewComment("");
    } catch (error) {
      console.error("Failed to submit comment:", error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDownloadFiles = (inspection) => {
    if (inspection.submittedFiles.length === 0) return;

    // Create a zip file or download individual files
    inspection.submittedFiles.forEach((file, index) => {
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = `/api/files/${file.id}/download`;
        link.download = file.name;
        link.click();
      }, index * 500); // Stagger downloads to avoid browser blocking
    });
  };

  const handleDownloadSingleFile = (file) => {
    const link = document.createElement("a");
    link.href = `/api/files/${file.id}/download`;
    link.download = file.name;
    link.click();
  };

  if (!permit) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading permit inspections...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/pp/contractor/permits")}
          sx={{ mr: 2 }}
        ></Button>
        <Box>
          <Typography color="primary" variant="h4">
            {permit.number}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {permit.address}
          </Typography>
        </Box>
      </Box>

      {/* Permit Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                Permit Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {permit.type}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {permit.status}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submitted: {permit.submittedDate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved: {permit.approvedDate}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                Contractor
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {permit.contractor.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                License: {permit.contractor.license}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone: {permit.contractor.phone}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Inspections List */}
      <Typography variant="h5" gutterBottom>
        Inspections ({inspections.length})
      </Typography>

      <Grid container spacing={3}>
        {inspections.map((inspection) => (
          <Grid key={inspection.id} size={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {getStatusIcon(inspection.status)}
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {inspection.name}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        {getStatusChip(inspection.status)}
                        <Chip
                          label={inspection.type}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                        {inspection.required && (
                          <Chip
                            label="Required"
                            color="error"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Inspection Details Accordion */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">View Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      {/* Schedule Info */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Schedule
                        </Typography>
                        {inspection.scheduledDate ? (
                          <Typography variant="body2" color="text.secondary">
                            Scheduled: {inspection.scheduledDate}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not scheduled
                          </Typography>
                        )}
                        {inspection.completedDate && (
                          <Typography variant="body2" color="text.secondary">
                            Completed: {inspection.completedDate}
                          </Typography>
                        )}
                        {inspection.inspector && (
                          <Typography variant="body2" color="text.secondary">
                            Inspector: {inspection.inspector}
                          </Typography>
                        )}
                      </Grid>

                      {/* Dependencies */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Dependencies
                        </Typography>
                        {inspection.dependencies.length > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Depends on: {inspection.dependencies.join(", ")}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No dependencies
                          </Typography>
                        )}
                      </Grid>

                      {/* Files */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Submitted Files ({inspection.submittedFiles.length})
                        </Typography>
                        {inspection.submittedFiles.length > 0 ? (
                          <List dense>
                            {inspection.submittedFiles
                              .slice(0, 3)
                              .map((file) => (
                                <ListItem key={file.id} sx={{ px: 0 }}>
                                  <ListItemIcon>
                                    <PhotoCamera fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={file.name}
                                    secondary={file.uploadDate}
                                  />
                                  <ListItemSecondaryAction>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleViewFile(
                                            file,
                                            inspection.submittedFiles
                                          )
                                        }
                                        title="View file"
                                      >
                                        <Visibility />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDownloadSingleFile(file)
                                        }
                                        title="Download file"
                                      >
                                        <Download />
                                      </IconButton>
                                    </Box>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            {inspection.submittedFiles.length > 3 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                +{inspection.submittedFiles.length - 3} more
                                files
                              </Typography>
                            )}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No files submitted
                          </Typography>
                        )}
                      </Grid>
                    </Grid>

                    {/* Failure Information */}
                    {inspection.status === "failed" &&
                      inspection.failureReasons && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Inspection Failed
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {inspection.correctionRequired}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Issues:</strong>
                          </Typography>
                          <ul>
                            {inspection.failureReasons.map((reason, index) => (
                              <li key={index}>
                                <Typography variant="body2">
                                  {reason}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                          {inspection.codeReferences && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Code References:</strong>{" "}
                              {inspection.codeReferences.join(", ")}
                            </Typography>
                          )}
                        </Alert>
                      )}

                    {/* Comments */}
                    {inspection.comments.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Comments
                        </Typography>
                        <Timeline>
                          {inspection.comments.map((comment) => (
                            <TimelineItem key={comment.id}>
                              <TimelineOppositeContent sx={{ m: "auto 0" }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {comment.date}
                                </Typography>
                              </TimelineOppositeContent>
                              <TimelineSeparator>
                                <TimelineDot>
                                  <Avatar sx={{ width: 24, height: 24 }}>
                                    {comment.author.charAt(0)}
                                  </Avatar>
                                </TimelineDot>
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent sx={{ py: "12px", px: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  component="span"
                                >
                                  {comment.author}
                                </Typography>
                                <Typography variant="body2">
                                  {comment.text}
                                </Typography>
                              </TimelineContent>
                            </TimelineItem>
                          ))}
                        </Timeline>
                      </Box>
                    )}

                    {/* History */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        History
                      </Typography>
                      <Timeline>
                        {inspection.history.map((event, index) => (
                          <TimelineItem key={index}>
                            <TimelineOppositeContent sx={{ m: "auto 0" }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {event.date}
                              </Typography>
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot color="primary" />
                              {index < inspection.history.length - 1 && (
                                <TimelineConnector />
                              )}
                            </TimelineSeparator>
                            <TimelineContent sx={{ py: "12px", px: 2 }}>
                              <Typography variant="body2">
                                <strong>{event.action}</strong> by {event.user}
                              </Typography>
                            </TimelineContent>
                          </TimelineItem>
                        ))}
                      </Timeline>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  {canResubmit(inspection) && (
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<Refresh />}
                      onClick={() => {
                        setSelectedInspection(inspection);
                        setInspectionDialog(true);
                      }}
                    >
                      Resubmit
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<Comment />}
                    disabled={inspection.status === "not_requested"}
                    onClick={() => handleAddComment(inspection)}
                  >
                    Add Comment
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    disabled={inspection.submittedFiles.length === 0}
                    onClick={() => handleDownloadFiles(inspection)}
                  >
                    Download Files
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCamera />}
                    color="success"
                    onClick={() => {
                      setSelectedInspection(inspection);
                      setPhotoCaptureOpen(true);
                    }}
                  >
                    Capture Photos
                  </Button>
                  {inspection.submittedFiles.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      color="secondary"
                      onClick={() => {
                        const photoFile = inspection.submittedFiles[0]; // Use first file as default
                        setSelectedPhoto(photoFile);
                        setAnnotationOpen(true);
                      }}
                    >
                      Annotate Photos
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Resubmit Inspection Dialog */}
      <Dialog
        open={inspectionDialog}
        onClose={() => setInspectionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Resubmit Inspection - {selectedInspection?.name}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Upload corrected files and provide comments addressing the
            inspection failures.
          </Alert>

          {selectedInspection?.failureReasons && (
            <Card sx={{ mb: 3, bgcolor: "error.light" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Address These Issues:
                </Typography>
                <ul>
                  {selectedInspection.failureReasons.map((reason, index) => (
                    <li key={index}>
                      <Typography variant="body2">{reason}</Typography>
                    </li>
                  ))}
                </ul>
                {selectedInspection.codeReferences && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    <strong>Code References:</strong>{" "}
                    {selectedInspection.codeReferences.join(", ")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* File Upload */}
          <Typography variant="h6" gutterBottom>
            Upload Corrected Files
          </Typography>

          <Paper
            sx={{
              border: 2,
              borderStyle: "dashed",
              borderColor: "grey.300",
              p: 3,
              textAlign: "center",
              mb: 2,
              cursor: "pointer",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
            onClick={() =>
              document.getElementById("resubmit-file-upload").click()
            }
          >
            <CloudUpload sx={{ fontSize: 48, color: "grey.500", mb: 1 }} />
            <Typography variant="body1">Upload corrected files</Typography>
            <Typography variant="caption" color="text.secondary">
              JPG, PNG, PDF files (max 5MB each)
            </Typography>
          </Paper>

          <input
            id="resubmit-file-upload"
            type="file"
            multiple
            accept="image/*,.pdf"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />

          {resubmitFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Files to Upload ({resubmitFiles.length})
              </Typography>
              <List dense>
                {resubmitFiles.map((file) => (
                  <ListItem key={file.id}>
                    <ListItemIcon>
                      <PhotoCamera fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={file.name} secondary={file.size} />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFile(file.id)}
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Correction Comments"
            value={resubmitComments}
            onChange={(e) => setResubmitComments(e.target.value)}
            placeholder="Describe the corrections you've made..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspectionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResubmitInspection}
            disabled={resubmitFiles.length === 0 || submitting}
            startIcon={submitting ? <Send /> : <Refresh />}
          >
            {submitting ? "Resubmitting..." : "Resubmit Inspection"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">{selectedFile?.name}</Typography>
            <IconButton onClick={handleCloseFileViewer}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: "relative" }}>
          {selectedFile && (
            <Box sx={{ height: "100%", position: "relative" }}>
              {/* File Counter */}
              {selectedFiles.length > 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    zIndex: 1,
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">
                    {currentFileIndex + 1} of {selectedFiles.length}
                  </Typography>
                </Box>
              )}

              {/* Navigation Buttons */}
              {selectedFiles.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevFile}
                    disabled={currentFileIndex === 0}
                    sx={{
                      position: "absolute",
                      left: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <IconButton
                    onClick={handleNextFile}
                    disabled={currentFileIndex === selectedFiles.length - 1}
                    sx={{
                      position: "absolute",
                      right: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                </>
              )}

              {/* File Content */}
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#f5f5f5",
                }}
              >
                {selectedFile.name.toLowerCase().endsWith(".pdf") ? (
                  <Box sx={{ width: "100%", height: "100%" }}>
                    <iframe
                      src={`https://via.placeholder.com/800x600/0066cc/ffffff?text=${encodeURIComponent(selectedFile.name)}`}
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                      title={selectedFile.name}
                    />
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={`https://via.placeholder.com/800x600/0066cc/ffffff?text=${encodeURIComponent(selectedFile.name)}`}
                    alt={selectedFile.name}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileViewer}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => {
              // Handle file download
              const link = document.createElement("a");
              link.href = `/api/files/${selectedFile.id}/download`;
              link.download = selectedFile.name;
              link.click();
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Comment - {commentInspection?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Add a comment to this inspection. This will be visible to inspectors
            and other stakeholders.
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Enter your comment here..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitComment}
            disabled={!newComment.trim() || submittingComment}
            startIcon={<Comment />}
          >
            {submittingComment ? "Submitting..." : "Submit Comment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Capture Dialog */}
      <PpMobilePhotoCaptureComponent
        open={photoCaptureOpen}
        onClose={() => setPhotoCaptureOpen(false)}
        onPhotosUploaded={(photos) => {
          // Update inspection with new photos
          if (selectedInspection) {
            setInspections((prev) =>
              prev.map((ins) =>
                ins.id === selectedInspection.id
                  ? {
                      ...ins,
                      submittedFiles: [
                        ...ins.submittedFiles,
                        ...photos.map((photo) => ({
                          id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                          name: `Photo_${Date.now()}.jpg`,
                          type: "photo",
                          size: photo.metadata?.fileSize || "Unknown",
                          url: photo.dataUrl,
                          uploadedAt: new Date().toISOString(),
                        })),
                      ],
                    }
                  : ins
              )
            );
          }
          setPhotoCaptureOpen(false);
        }}
        category="inspection"
        inspectionId={selectedInspection?.id}
        propertyId={permit?.id}
      />

      {/* Photo Annotation Dialog */}
      {selectedPhoto && (
        <Dialog
          open={annotationOpen}
          onClose={() => setAnnotationOpen(false)}
          maxWidth="xl"
          fullWidth
          fullScreen
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Edit />
              Photo Annotation - {selectedInspection?.name}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <PpPhotoAnnotationTools
              photo={selectedPhoto}
              photoList={selectedInspection?.submittedFiles || []}
              onSave={(annotatedPhoto) => {
                setAnnotationOpen(false);
              }}
              onCancel={() => setAnnotationOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default ContractorPermitInspectionsPage;
